import './LinkLibraryTotals.scss';

import {
  AcceptedCurrencyCode,
  useTotalsQuery,
} from '../../../types/__generated-graphQL__';
import React, { FunctionComponent, useContext } from 'react';

import { AuthContext } from '@justgiving/auth-react';
import { PlaceholderBar } from '../PlaceholderBar/PlaceholderBar';
import Refresh from '../Refresh/Refresh';
import { TAGS } from '../../constants/tags';
import { createGraphQlClient } from '../../graphQl/baseApi';
import { formatCurrency } from '../../utils/currencyFormatting';
import { getTotalFromGqlTotals } from '../../utils/getTotalFromGqlTotals';
import { useCharityData } from '../../context/charityContext';
import { useTranslation } from 'react-i18next';

const FALLBACK_CURRENCY_CODE = AcceptedCurrencyCode.Gbp;

export const LinkLibraryTotals: FunctionComponent = () => {
  const { t } = useTranslation();
  const { charityId, charity } = useCharityData();
  const auth = useContext(AuthContext);
  const { data, isLoading, isError, refetch } = useTotalsQuery(
    createGraphQlClient(auth),
    {
      segment: TAGS.segment,
      tagDefinitionId: TAGS.totalsTagDefinitionId,
      tagValue: `${charityId}`,
    },
  );
  const currencyCode =
    charity?.settlementCurrencyCode ?? FALLBACK_CURRENCY_CODE;

  if (isError) {
    return (
      <Refresh
        onRefresh={refetch}
        buttonLabel={t('linkLibrary.totals.refresh')}
      >
        <TotalsWrapper isLoading isError />
      </Refresh>
    );
  }

  if (isLoading || !data?.totals) {
    return <TotalsWrapper isLoading />;
  }

  const { totals } = data;

  const valueOfDonations = getTotalFromGqlTotals(
    totals,
    'fundraising:donations_received',
    currencyCode,
  );

  const numberOfDonations = getTotalFromGqlTotals(
    totals,
    'fundraising:donations_made',
    'count',
  );

  return (
    <TotalsWrapper
      valueOfDonations={valueOfDonations}
      numberOfDonations={numberOfDonations}
      currencyCode={currencyCode}
    />
  );
};

type TotalsWrapperProps = {
  valueOfDonations?: number;
  numberOfDonations?: number;
  currencyCode?: AcceptedCurrencyCode;
  isLoading?: boolean;
  isError?: boolean;
};

export const TotalsWrapper: FunctionComponent<TotalsWrapperProps> = ({
  isLoading = false,
  isError = false,
  valueOfDonations,
  numberOfDonations,
  currencyCode,
}) => {
  const { t } = useTranslation();

  const valueOfDonationsDisplayValue =
    typeof valueOfDonations === 'number'
      ? formatCurrency({
          currencyCode,
          amount: valueOfDonations,
          alwaysShowFractions: true,
        })
      : null; // Loading or error

  const numberOfDonationsDisplayValue =
    typeof numberOfDonations === 'number' ? numberOfDonations : null;

  const averageDonationValue =
    valueOfDonations && numberOfDonations
      ? valueOfDonations / numberOfDonations
      : null; // Loading or error

  const averageDonationDisplayValue =
    typeof averageDonationValue === 'number'
      ? formatCurrency({
          currencyCode,
          amount: averageDonationValue,
          alwaysShowFractions: true,
        })
      : isLoading
        ? null
        : '-'; // This is the situation where there are no GC donations, so there is no average value - but displaying 0 is not correct

  return (
    <>
      <ul // eslint-disable-line jsx-a11y/no-redundant-roles
        className="totalsWrapper"
        data-qa="totalsWrapper"
        title={t('linkLibrary.totals.heading')}
        role="list"
      >
        <TotalBox
          className="totalBox totalBoxWide"
          label={t('linkLibrary.totals.totalRaised')}
          value={valueOfDonationsDisplayValue}
          isLoading={isLoading}
          isError={isError}
          dataQa="totalRaisedAmount"
        />
        <TotalBox
          className="totalBox"
          label={t('linkLibrary.totals.supporters')}
          value={numberOfDonationsDisplayValue}
          isLoading={isLoading}
          isError={isError}
          dataQa="supporterCount"
        />
        <TotalBox
          className="totalBox"
          label={t('linkLibrary.totals.averageDonation')}
          value={averageDonationDisplayValue}
          isLoading={isLoading}
          isError={isError}
          dataQa="averageDonationAmount"
        />
      </ul>
    </>
  );
};

type TotalBoxProps = {
  className?: string;
  label: string;
  value: string | number | null;
  isLoading?: boolean;
  isError?: boolean;
  dataQa: string;
};

export const TotalBox: FunctionComponent<TotalBoxProps> = ({
  className,
  label,
  value,
  isLoading,
  isError,
  dataQa,
}) => {
  const { t } = useTranslation();
  return (
    <li className={className} key={label}>
      <p className="totalLabel" aria-hidden>
        {label}
      </p>
      {value !== null && (
        <>
          <p className="totalValue" data-qa={dataQa} aria-hidden>
            {value}
          </p>
          <span className="jg-sr-only">{`${label}: ${value}`}</span>
        </>
      )}
      {(isLoading || value === null) && (
        <>
          <span className="jg-sr-only">
            {`${label}: ${
              isError ? t('common.dataNotAvailable') : t('common.loading')
            }`}
          </span>
          <PlaceholderBar
            marginTop={'8px'}
            marginLeft={'auto'}
            marginRight={'auto'}
            height={'24px'}
            width={'50%'}
            animated
          />
        </>
      )}
    </li>
  );
};
